import React from "react";

import {
  Box,
  Divider,
  List,
  styled,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/useAppSelector";
import { drawerWidth } from "./Header";
import {
  backToPrevState,
  keepOpenDrawer,
  toggleDrawer,
} from "../../store/drawer.slice";

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 0,
  [theme.breakpoints.up("sm")]: {
    width: 0,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function SideBar() {
  const isMobile = useMediaQuery("(max-width:769px)");
  const auth = useAppSelector((state) => state.auth.user);
  const { menuList } = useAppSelector((state) => state.menu);
  React.useEffect(() => {
    if (isMobile) {
      dispatch(toggleDrawer());
    }

    if (!isMobile && !open) {
      dispatch(toggleDrawer());
    }
  }, [isMobile]);
  const open = useAppSelector((state) => state.drawer.open);
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const onSelect = (event) => {
    navigate(event.target.props.route);
  };
  const path = location.pathname
    .split("/")
    .filter((d) => d === "" || isNaN(d))
    .join("/");

  const panelBarItems = [
    { title: t("create_event"), path: "/create-event" },
    { title: t("events"), path: "/events" },
  ];

  const dynamicPanelBarItems = panelBarItems.map((item) => (
    <PanelBarItem
      key={item.path}
      title={
        <Link to={item.path} className="nav-link">
          {item.title}
        </Link>
      }
      selected={item.path === path}
      route={item.path}
    />
  ));

  const upperMenu = menuList?.filter((x) => x.uppeR_MENU_CODE === "0000");
  const subMenu = menuList?.filter((x) => x.uppeR_MENU_CODE !== "0000");
  const menuItems = upperMenu.map((item) => {
    const childItems = subMenu
      ?.filter((x) => x.uppeR_MENU_CODE === item.menU_CODE)
      .map((subItem) => (
        <PanelBarItem
          key={subItem.menU_CODE}
          iconClass={subItem.menU_ICON_CLASS_NAME}
          route={subItem.menU_URL}
          title={
            <Link
              key={subItem.menU_CODE}
              to={subItem.menU_URL}
              className="nav-link"
            >
              {subItem.menU_ADI}
            </Link>
          }
        />
      ));

    return (
      <PanelBarItem
        key={item.menU_CODE}
        iconClass={item.menU_ICON_CLASS_NAME}
        title={
          <Link key={item.menU_CODE} to={item.menU_URL} className="nav-link">
            {item.menU_ADI}
          </Link>
        }
      >
        {childItems || null} {/* Ensure childItems is not undefined */}
      </PanelBarItem>
    );
  });
  return (
    <Drawer
      variant="permanent"
      open={open}
      onMouseEnter={() => dispatch(keepOpenDrawer())}
      onMouseLeave={() => dispatch(backToPrevState())}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: "0px!important",
          ...(!open && {
            visibility: "hidden",
            transition: theme.transitions.create(["visibility"], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
          }),
        }}
      >
        <a className="nav-link" href={"/"}>
          <img
            id="idLogo"
            src="https://www.daikin.com.tr/Content/img/icons/daikin-logo.svg"
            alt=""
          ></img>
        </a>
      </Toolbar>
      <Divider />
      <List component="nav">
        <Box
          className="info"
          sx={{
            ml: 2,
            my: 1,
            ...(!open && {
              visibility: "hidden",
              transition: theme.transitions.create(["visibility"], {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
            }),
          }}
        >
          <span style={{ fontStyle: "italic" }}>
            {t("welcome")}, <b>{auth.given_name}</b>
          </span>
        </Box>
        <Divider variant="fullWidth" />

        <PanelBar expandMode={"single"} onSelect={onSelect}>
          <PanelBarItem
            iconClass="fa fa-home fa-5"
            title={
              <>
                <Link className="nav-link" state={{ from: location }}>
                  {t("homepage")}
                </Link>
                {/* <HomeIcon sx={{ mr: 4 }} /> {t("homepage")} */}
              </>
            }
            selected={"/" === path}
            route={"/"}
          />
          <PanelBarItem
            iconClass="fa fa-calendar fa-5"
            title={
              <>
                <Link className="nav-link" state={{ from: location }}>
                  {t("events")}
                </Link>
              </>
            }
            selected={"/events" === path}
            route={"/events"}
          />
          <PanelBarItem
            iconClass="fa fa-calendar fa-5"
            title={
              <>
                <Link className="nav-link" state={{ from: location }}>
                  {t("company_calendar")}
                </Link>
              </>
            }
            selected={"/calendar" === path}
            route={"/calendar"}
          />
          <PanelBarItem
            iconClass="fa fa-envelope fa-5"
            title={
              <>
                <Link className="nav-link" state={{ from: location }}>
                  {t("mail_templates")}
                </Link>
              </>
            }
            selected={"/template" === path}
            route={"/template"}
          />
          {/*           
          <PanelBarItem title={t("events")}>
            {dynamicPanelBarItems}
          </PanelBarItem> */}
        </PanelBar>
      </List>
    </Drawer>
  );
}
